import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowUp, Check, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

type Env = "production" | "staging" | "development" | "preview";
type NamedEnv = "production" | "staging" | "development";

interface PromoteResponse {
  ok: true;
  deploymentId: string;
  fromEnv: Env;
  env: NamedEnv;
  pinnedHostnames: string[];
  warning?: "vars-may-differ";
}

/**
 * T043 (spec 002 US5) — promote a `ready` deployment to a named env.
 *
 * Modal flow:
 *  1. Click "Promote" → open env-picker modal pre-selected to a sensible
 *     target (production if the source isn't already production; else
 *     staging).
 *  2. Confirm → POST /promote.
 *  3. On success: if the response includes warning='vars-may-differ',
 *     surface it inline (operator may want to roll back or push fresh
 *     env vars). Invalidate ["deployments"] so the list re-renders.
 *     The deployment id doesn't change, so no nav is needed.
 */
export function PromoteButton({
  orgSlug,
  projectSlug,
  deploymentId,
  currentEnv,
  size = "sm",
}: {
  orgSlug: string;
  projectSlug: string;
  deploymentId: string;
  currentEnv: Env;
  size?: "sm" | "default";
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  // Default target: production unless we already are production.
  const defaultTarget: NamedEnv = currentEnv === "production" ? "staging" : "production";
  const [target, setTarget] = useState<NamedEnv>(defaultTarget);

  const options = useMemo<NamedEnv[]>(
    () => (["production", "staging", "development"] as NamedEnv[]).filter((e) => e !== currentEnv),
    [currentEnv],
  );

  const promote = useMutation({
    mutationFn: (opts?: { force?: boolean }) =>
      api.post<PromoteResponse>(
        `/orgs/${orgSlug}/projects/${projectSlug}/deployments/${deploymentId}/promote`,
        { toEnv: target, ...(opts?.force ? { force: true } : {}) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deployments"] });
      qc.invalidateQueries({ queryKey: ["deployment", deploymentId] });
      qc.invalidateQueries({ queryKey: ["project-overview", orgSlug, projectSlug] });
    },
  });

  // 006 US10 — a 409 checks_failed means deployment checks are failing;
  // offer an explicit, audited override instead of a dead end.
  const checksBlocked =
    promote.error instanceof ApiError && promote.error.code === "checks_failed";
  const failingChecks = checksBlocked
    ? (((promote.error as ApiError).details as { failing?: Array<{ name: string; status: string; detail?: string | null }> })
        ?.failing ?? [])
    : [];

  function close() {
    setOpen(false);
    setTimeout(() => {
      promote.reset();
      setTarget(defaultTarget);
    }, 200);
  }

  // Preview deployments aren't promotable in v1 (the env value is treated
  // as a sentinel) — guard the trigger so the button doesn't even render.
  if (options.length === 0) return null;

  return (
    <>
      <Button variant="default" size={size} onClick={() => setOpen(true)}>
        <ArrowUp className="h-3.5 w-3.5" /> Promote
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="of-card w-[calc(100vw-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
            <div className="of-card-header">
              <h3 className="text-sm font-semibold">Promote deployment</h3>
            </div>
            <div className="px-4 py-3 space-y-3">
              {promote.isSuccess ? (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1.5 text-success text-sm">
                    <Check className="h-4 w-4" /> Promoted to{" "}
                    <span className="font-mono">{target}</span>.
                  </div>
                  {promote.data?.warning === "vars-may-differ" && (
                    <div className="rounded-md border border-attention/40 bg-attention-subtle/40 px-3 py-2 text-xs space-y-1">
                      <div className="inline-flex items-center gap-1.5 font-semibold text-attention">
                        <AlertTriangle className="h-3.5 w-3.5" /> Env vars may differ
                      </div>
                      <div className="text-muted-foreground">
                        One or more env vars applied to{" "}
                        <span className="font-mono">{target}</span> have changed since this
                        artifact was built. The deployment may behave differently than when it
                        first shipped. Consider redeploying with the current vars if you see
                        regressions.
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end pt-1">
                    <Button variant="primary" size="sm" onClick={close}>
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm">
                    Re-route a target env to this build. No new container is created; traffic
                    flips within seconds.
                  </p>
                  <div className="space-y-1">
                    <Label className="text-xs">Target env</Label>
                    <select
                      value={target}
                      onChange={(e) => setTarget(e.target.value as NamedEnv)}
                      className="w-full h-9 px-2 rounded-md border border-border bg-background text-sm font-mono"
                    >
                      {options.map((e) => (
                        <option key={e} value={e}>{e}</option>
                      ))}
                    </select>
                    <div className="text-2xs text-subtle-foreground">
                      Currently on <span className="font-mono">{currentEnv}</span>.
                    </div>
                  </div>
                  {checksBlocked ? (
                    <div className="rounded-md border border-danger/40 bg-danger-subtle/40 px-3 py-2 text-xs space-y-1.5">
                      <div className="inline-flex items-center gap-1.5 font-semibold text-danger">
                        <AlertTriangle className="h-3.5 w-3.5" /> Deployment checks are failing
                      </div>
                      <ul className="space-y-0.5 text-muted-foreground">
                        {failingChecks.map((c) => (
                          <li key={c.name} className="font-mono">
                            {c.name} — {c.status}
                            {c.detail ? `: ${c.detail}` : ""}
                          </li>
                        ))}
                      </ul>
                      <div className="text-muted-foreground">
                        Promoting anyway is recorded in the audit log.
                      </div>
                    </div>
                  ) : promote.error instanceof ApiError ? (
                    <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
                      {promote.error.message}
                    </div>
                  ) : null}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="default" size="sm" onClick={close}>
                      Cancel
                    </Button>
                    {checksBlocked ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => promote.mutate({ force: true })}
                        disabled={promote.isPending}
                      >
                        {promote.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Promote anyway
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => promote.mutate(undefined)}
                        disabled={promote.isPending}
                      >
                        {promote.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Confirm promote
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
